import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useLocation, useHistory } from 'react-router';
import { useFonts } from '@folklore/fonts';

import { useUser } from '../../contexts/AuthContext';
import { useMenu, useMenuOpen } from '../../contexts/MenusContext';
import { useSplash, useFade, useSetFade } from '../../contexts/PagesContext';

import MobileMenu from '../menus/Mobile';
import Header from '../partials/Header';
import Splash from '../partials/Splash';
import Trees from '../partials/Trees';
import DumbTrees from '../partials/DumbTrees';
import Logos from '../partials/Logos';

import styles from '../../../styles/layouts/main.module.scss';

const propTypes = {
    isVideo: PropTypes.bool,
    children: PropTypes.node,
};

const defaultProps = {
    isVideo: false,
    children: null,
};

const MainLayout = ({ isVideo, children }) => {
    const { pathname } = useLocation();
    const history = useHistory();
    const scrollRef = useRef(null);

    const user = useUser();
    const splash = useSplash();
    const fade = useFade();
    const setFade = useSetFade();

    const menu = useMenu('main', user);
    const menuOpen = useMenuOpen();

    const { loaded: fontsLoaded } = useFonts({
        google: {
            families: ['Montserrat:300,400,500,600,700,800'],
        },
    });
    // const notLoading = !menuOpen;

    // Manage fade
    useEffect(() => {
        if (pathname === '/') {
            setFade(false);
        } else {
            setFade(true);
        }
    }, [setFade, pathname]);

    // Always scroll to top
    useEffect(() => {
        const unlisten = history.listen(() => {
            if (scrollRef.current) {
                if (scrollRef.current.scroll) {
                    scrollRef.current.scroll({
                        top: 0,
                        left: 0,
                        behavior: 'smooth',
                    });
                } else {
                    scrollRef.current.scrollTop = 0;
                }
            }
        });
        return () => {
            unlisten();
        };
    }, [history]);

    const innerStyle = {
        opacity: fontsLoaded ? 1 : 0,
    };

    const pageStyle = {
        opacity: !menuOpen ? 1 : 0,
    };

    return (
        <div className={classNames([styles.container, { [styles.splash]: splash }])}>
            {splash ? (
                <Splash />
            ) : (
                <>
                    <div className={styles.inner} style={innerStyle}>
                        <Header className={styles.header} user={user} menu={menu} />
                        <MobileMenu
                            className={classNames([styles.menu, { [styles.open]: menuOpen }])}
                            items={menu}
                        />
                        <div className={styles.page} style={pageStyle}>
                            <div className={styles.content} ref={scrollRef}>
                                {children}
                            </div>
                        </div>
                    </div>
                    <div className={classNames([styles.trees, { [styles.fade]: fade }])}>
                        <Logos
                            className={classNames([styles.logos, { [styles.menuOpen]: menuOpen }])}
                        />
                        {!isVideo ? <Trees rotateScene /> : <DumbTrees />}
                    </div>
                </>
            )}
        </div>
    );
};
MainLayout.propTypes = propTypes;
MainLayout.defaultProps = defaultProps;

export default MainLayout;
